import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Person,
  CalendarToday,
} from '@mui/icons-material';
import { useAuditLog, useAuditActions, useAuditStats } from '../../hooks/useUsers';
import { useOrganizations } from '../../hooks/useFleet';
import { SectionTabs } from '../../components/SectionTabs';
import { MANAGEMENT_TABS, MANAGEMENT_ACCENT_COLOR } from './managementTabs';
import { managementHelp } from '../../components/PageHelp/helpContent';

const actionColors: Record<string, string> = {
  create: '#10b981',
  read: '#6366f1',
  update: '#f59e0b',
  delete: '#ef4444',
  login: '#3b82f6',
  logout: '#6b7280',
};

const getActionColor = (action: string): string => {
  const parts = action.split('.');
  const verb = parts[1] || parts[0];
  return actionColors[verb] || '#6366f1';
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString();
};

const AuditLogPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orgFilter, setOrgFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const { data, isLoading, error } = useAuditLog({
    page: page + 1,
    size: rowsPerPage,
    organization_id: orgFilter || undefined,
    action: actionFilter || undefined,
    from: dateFrom || undefined,
    to: dateTo || undefined,
  });

  const { data: orgsData } = useOrganizations({ size: 100 });
  const { data: actionsData } = useAuditActions();
  const { data: statsData } = useAuditStats({ organization_id: orgFilter || undefined, days: 7 });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setOrgFilter('');
    setActionFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const entries = data?.data || [];
  const totalCount = data?.total || 0;
  const actions = actionsData || [];

  const hasFilters = orgFilter || actionFilter || dateFrom || dateTo;

  return (
    <Box sx={{ p: 3 }}>
      <SectionTabs
        title="Management"
        tabs={MANAGEMENT_TABS}
        accentColor={MANAGEMENT_ACCENT_COLOR}
        help={managementHelp.auditLog}
      />

      {/* Stats Cards */}
      {statsData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#1e1e1e' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Events ({statsData.period_days} days)
                </Typography>
                <Typography variant="h4" sx={{ color: '#6366f1' }}>
                  {statsData.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#1e1e1e' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Active Users
                </Typography>
                <Typography variant="h4" sx={{ color: '#10b981' }}>
                  {statsData.top_actors?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#1e1e1e' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Action Types
                </Typography>
                <Typography variant="h4" sx={{ color: '#3b82f6' }}>
                  {statsData.by_action?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#1e1e1e' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Recent Activity
                </Typography>
                <Typography variant="h4" sx={{ color: '#f59e0b' }}>
                  {statsData.by_day?.slice(-1)?.[0]?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load audit log. Please try again.
        </Alert>
      )}

      <Paper sx={{ bgcolor: '#1e1e1e', borderRadius: 2 }}>
        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: '1px solid #333', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={orgFilter}
              label="Organization"
              onChange={(e) => { setOrgFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Organizations</MenuItem>
              {(orgsData?.data || []).map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={actionFilter}
              label="Action"
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {actions.map((action) => (
                <MenuItem key={action} value={action}>{action}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          <TextField
            size="small"
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          {hasFilters && (
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ color: 'text.secondary' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CalendarToday sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                          No audit entries found{hasFilters ? ' matching your filters' : ''}.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(entry.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">
                                {entry.user_email || 'System'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.action}
                            size="small"
                            sx={{
                              bgcolor: getActionColor(entry.action),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {entry.resource_type && (
                            <Typography variant="body2">
                              {entry.resource_type}
                              {entry.resource_name && `: ${entry.resource_name}`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.details && Object.keys(entry.details).length > 0 && (
                            <Tooltip title={JSON.stringify(entry.details, null, 2)}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {JSON.stringify(entry.details).substring(0, 50)}...
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {entry.ip_address || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AuditLogPage;
